<template>
  <div class="lib__text-center">
    <Loader :label="waitingLabelWithCounter" v-if="waiting" />
    <Loader :label="$t('appointment.loadingAvailabilities')" v-else-if="loading" />
  </div>
</template>

<script lang="ts">
import Component from 'vue-class-component'
import { Prop, Emit, Vue, Watch } from 'vue-property-decorator'
import { dateService } from '@/services'
import {
  AppointmentResponse,
  AppointmentSlot,
  CreateAppointmentStatus,
  GetAppointmentAvailabilitiesRequest,
  GetAppointmentAvailabilitiesResponse,
  WorksAppointmentAvailability
} from '@/models'
import { AxiosResponse } from 'axios'

@Component({
  name: 'SearchAvailabilities'
})
export default class SearchAvailabilities extends Vue {
  //#region [Property]
  @Prop({ type: Object, required: true }) public parameters!: GetAppointmentAvailabilitiesRequest
  @Prop({ type: String, required: true }) public waitingLabel!: string
  // Status of the appointment creation
  @Prop({ type: String, default: null })
  public createAppointmentStatus!: CreateAppointmentStatus | null
  //#endregion

  //#region [Data]
  private loading: boolean = false
  private foundSlots: AppointmentResponse | null = null
  private waiting: boolean = false
  private waitingCounter: number = -1
  //#endregion

  //#region [Computed]
  private get waitingLabelWithCounter(): string {
    return this.waitingCounter !== -1 ? `${this.waitingLabel} ${this.waitingCounter}` : this.waitingLabel
  }

  //#endregion

  //#region [Watch]
  @Watch('createAppointmentStatus', { immediate: true })
  private async onCreateAppointmentStatusChange(value: CreateAppointmentStatus | null) {
    if (value === CreateAppointmentStatus.AppointmentNotAvailable) {
      await this.searchForAvailabilities()
      await this.emitLoaded()
    }
  }

  @Watch('parameters', { immediate: true, deep: true })
  private async onParametersChange(value: GetAppointmentAvailabilitiesRequest) {
    // Reset the counter in case the search was triggered again
    this.waitingCounter = -1
    // Set "optimisation de votre dossier" message (no counter yet) only when "see other dates" button was not clicked
    this.waiting = !value.seeOtherDatesClicked
    await this.searchForAvailabilities()
    await this.emitLoaded()
  }
  //#endregion

  //#region [Methods]

  // Search for availabilities/wait if needed.
  private async searchForAvailabilities() {
    this.emitLoading(true)
    // initial (and maybe sole) request
    let availabilitiesResponse = null
    try {
      availabilitiesResponse = await this.requestAppointmentAvailabilities()
      this.waiting = false
      
      if (!availabilitiesResponse) {
        // Unexpected error
        this.foundSlots = null
        this.emitLoading(false)
        return
      }

      // Map availabilities to slots
      this.foundSlots = this.getSlots(availabilitiesResponse?.availabilities || [])
      this.emitLoading(false)
    } catch (err) {
      this.foundSlots = null
      this.emitLoading(false)
    }
  }

  private getSlots(availabilities: WorksAppointmentAvailability[]): AppointmentResponse {
    return {
      slots: availabilities.map<AppointmentSlot>((a) => {
        return {
          startDate: new Date(a.startDate),
          endDate: new Date(a.endDate),
          timeslotLabel: '',
          key: dateService.displayDateText(a.startDate),
          otherDate: false
        }
      })
    }
  }

  private async requestAppointmentAvailabilities(): Promise<GetAppointmentAvailabilitiesResponse | null> {
    try {
      const token = sessionStorage.getItem('token')

      if (!token) {
        throw new Error('No token found in sessionStorage');
      }
      this.parameters.periodStartDate.setHours(2, 0, 0, 0)
      this.parameters.periodEndDate.setHours(24, 0, 0, 0)
      const response: AxiosResponse<GetAppointmentAvailabilitiesResponse> = await this.$api.post(
        `api/worksrequest/work-file/${this.parameters.workFileReference}/appointment/availabilities`,
        this.parameters,
        {
          headers: {
            Authorization: token
          }
        }
      )
      if (response.status !== 200) {
        this.$emit('availabilitiesError')
        return null
      }
      return response.data
    } catch (err) {
      this.emitLoading(false)
      return null
    }
  }

  //#region [Emit]
  @Emit('loaded')
  private async emitLoaded(): Promise<AppointmentResponse | null> {
    return this.foundSlots
  }

  @Emit('loading')
  private emitLoading(loading: boolean): boolean {
    this.loading = loading
    return loading
  }
  //#endregion
}
</script>
